import { useState } from 'react';
import {
  useAuth,
  useFetch,
  useListFetch,
  useModal,
  useRequest,
} from '../../../../../hooks';
import { notify } from '../../../../../app';
import { useRouteMatch } from 'react-router-dom';
import type { ContentData } from '../components/SourceDetailsModal';
import { handleDownloadFile } from '../utils';
import {
  activateKnowledgeSource,
  deleteKnowledgeSource,
  toggleActiveKnowledgeSource,
  getAllKnowledgeSources,
  renameKnowledgeSource,
  uploadKnowledgeSourceFile,
  uploadKnowledgeSourceText,
  getBaseKnowledgeById,
  updateChunk,
  downloadKnowledgeSource,
  downloadKnowledgeBase,
} from '../../../../../api';
import type {
  ActivateKnowledgeSource,
  UploadKnowledgeSourceFile,
  UploadKnowledgeSourceText,
} from '../../../../../api';
import type { SortBy, SortOrder } from '../../../../../services/knowledge-base';

export const useKnowledgeSourceManagement = (id: string) => {
  const { params } = useRouteMatch<{ id: string }>();
  const { isOpened, onClose, onOpen } = useModal();
  const {
    isOpened: isOpenedSourceModal,
    onClose: onCloseSourceModal,
    onOpen: onOpenSourceModal,
  } = useModal();
  const {
    isOpened: isOpenedEditChunkModal,
    onClose: onCloseEditChunkModal,
    onOpen: onOpenEditChunkModal,
  } = useModal();

  const [editingChunk, setEditingChunk] = useState<ContentData | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [downloadingKnowledgeSource, setDownloadingKnowledgeSource] =
    useState(false);
  const {
    isOpened: isOpenedActivateModal,
    onClose: onCloseActivateModal,
    onOpen: onOpenActivateModal,
  } = useModal();
  const {
    isOpened: isOpenedRenameModal,
    onClose: onCloseRenameModal,
    onOpen: onOpenRenameModal,
  } = useModal();
  const {
    isOpened: isOpenedDeleteModal,
    onClose: onCloseDeleteModal,
    onOpen: onOpenDeleteModal,
  } = useModal();
  const { user } = useAuth();
  const userId = user?.identities?.[0]?.user_id || '';

  const {
    nodes: dataSources,
    filters,
    setFilters,
    setSort,
    intializing,
    refresh,
    ...rest
  } = useListFetch(
    async params => {
      return await getAllKnowledgeSources({
        page: params?.pagination?.page || 1,
        limit: params?.pagination?.perPage ?? 1,
        idKnowledgeBase: id,
        fileType: params?.filters?.file_type,
        name: params?.filters?.name,
        sortBy: (params?.sort?.name || 'created_at') as SortBy,
        sortOrder: (params?.sort?.direction ?? 'desc') as SortOrder,
      });
    },
    {
      deps: [id],
      resultKey: 'data_sources',
      withQueryParams: true,
    }
  );

  const [handleUploadSourceFirecrawl, addingSourceFirecrawl] = useRequest(
    (_signal, payload: UploadKnowledgeSourceFile) => {
      return uploadKnowledgeSourceFile({
        id_knowledge_base: id,
        user_id: userId,
        file: payload.file,
      });
    },
    {
      onSuccess: () => {
        notify.success('The source firecrawl has been uploaded successfully');
        refresh();
        onClose?.();
      },
      onError: () => {
        notify.error(
          'Firecrawl upload functionality is no longer available. Please use file upload instead.'
        );
      },
      deps: [],
    }
  );

  const [handleUploadSourceFile, addingSourceFile] = useRequest(
    (_signal, payload: UploadKnowledgeSourceFile) => {
      return uploadKnowledgeSourceFile({
        id_knowledge_base: id,
        user_id: userId,
        file: payload.file,
      });
    },
    {
      onSuccess: () => {
        notify.success('The source file has been uploaded successfully');
        refresh();
        onClose?.();
      },
      onError: () => {
        notify.error('Failed to upload the source file');
      },
      deps: [],
    }
  );

  const [handleUploadSourceText, addingSourceText] = useRequest(
    (_signal, payload: UploadKnowledgeSourceText) => {
      return uploadKnowledgeSourceText({
        ...payload,
        id_knowledge_base: id,
        user_id: userId,
      });
    },
    {
      onSuccess: () => {
        notify.success('The source file has been uploaded successfully');
        refresh();
        onClose?.();
      },
      onError: () => {
        notify.error('Failed to upload the source file');
      },
      deps: [],
    }
  );

  const [handleActivateKnowledgeSource, activatingKnowledgeSource] = useRequest(
    (_signal, payload: ActivateKnowledgeSource) => {
      return activateKnowledgeSource({
        ...payload,
      });
    },
    {
      onSuccess: () => {
        notify.success('The source has been activated successfully');
        refresh();
        onCloseActivateModal?.();
      },
      onError: () => {
        notify.error('Failed to activate the source file');
      },
      deps: [],
    }
  );

  const [handleDeleteKnowledgeSource, deletingKnowledgeSource] = useRequest(
    (_signal, dataSourceId: string) => {
      return deleteKnowledgeSource(dataSourceId);
    },
    {
      onSuccess: () => {
        notify.success('The source has been deleted successfully');
        refresh();
        onCloseDeleteModal?.();
      },
      onError: () => {
        notify.error('Failed to delete the source');
      },
      deps: [],
    }
  );

  const [handleRenameKnowledgeSource, renamingKnowledgeSource] = useRequest(
    (_signal, dataSourceId: string, newName: string) => {
      return renameKnowledgeSource(dataSourceId, newName);
    },
    {
      onSuccess: () => {
        notify.success('The source has been renamed successfully');
        refresh();
        onCloseRenameModal?.();
      },
      onError: () => {
        notify.error('Failed to rename the source');
      },
      deps: [],
    }
  );

  const [handleToggleActiveKnowledgeSource, disactivatingKnowledgeSource] =
    useRequest(
      (_signal, dataSourceId: string, active: boolean = false) => {
        return toggleActiveKnowledgeSource(dataSourceId, active);
      },
      {
        onSuccess: () => {
          notify.success('The source has been disactivated successfully');
          refresh();
        },
        onError: () => {
          notify.error('Failed to disactivate the source');
        },
        deps: [],
      }
    );

  const [handleUpdateChunk, updatingChunk] = useRequest(
    (
      _signal,
      knowledgeBaseId: string,
      chunkId: string,
      payload: { document?: string; metadata?: Record<string, any> }
    ) => {
      return updateChunk(knowledgeBaseId, chunkId, payload);
    },
    {
      onSuccess: () => {
        notify.success('The chunk has been updated successfully');
        onCloseEditChunkModal();
        setRefreshTrigger(prev => prev + 1);
      },
      onError: () => {
        notify.error('Failed to update the chunk');
      },
      deps: [],
    }
  );

  const { data: knowledgeBaseData, loading: isLoadingBase } =
    useFetch(async () => {
      return await getBaseKnowledgeById(id);
    }, [id]);

  const handleEditChunk = (chunk: ContentData) => {
    setEditingChunk(chunk);
    onOpenEditChunkModal();
  };

  const handleCloseEditChunkModalWithCleanup = () => {
    setEditingChunk(null);
    onCloseEditChunkModal();
  };

  const handleSaveEditChunk = (data: {
    metadata: Record<string, any>;
    document: string;
  }) => {
    if (editingChunk?.id) {
      handleUpdateChunk(params.id, editingChunk.id, data);
    }
  };

  const handleCloseSourceModalWithCleanup = () => {
    setRefreshTrigger(0);
    onCloseSourceModal();
  };

  const handleDownloadKnowledgeSource = async (
    idKnowledgeBase: string,
    dataSourceId?: string,
    fileName?: string
  ) => {
    await handleDownloadFile(
      () => downloadKnowledgeSource(idKnowledgeBase, dataSourceId),
      dataSourceId
        ? `${fileName || 'knowledge-source'}`
        : 'knowledge-base-all-sources',
      setDownloadingKnowledgeSource,
      dataSourceId
        ? 'Knowledge source downloaded successfully'
        : 'All knowledge sources downloaded successfully',
      dataSourceId
        ? 'Failed to download knowledge source'
        : 'Failed to download all knowledge sources'
    );
  };

  const handleDownloadAllKnowledgeSources = async () => {
    await handleDownloadFile(
      () => downloadKnowledgeBase(id),
      'knowledge-base-all-sources',
      setDownloadingKnowledgeSource,
      'All knowledge sources downloaded successfully',
      'Failed to download all knowledge sources'
    );
  };

  return {
    setSort,
    filters,
    setFilters,
    intializing,
    isOpened,
    onClose,
    onOpen,
    dataSources,
    handleUploadSourceFile,
    addingSourceFile,
    handleUploadSourceText,
    addingSourceText,
    handleActivateKnowledgeSource,
    activatingKnowledgeSource,
    isOpenedRenameModal,
    onCloseRenameModal,
    onOpenRenameModal,
    handleRenameKnowledgeSource,
    renamingKnowledgeSource,
    handleDeleteKnowledgeSource,
    deletingKnowledgeSource,
    isOpenedDeleteModal,
    onCloseDeleteModal,
    onOpenDeleteModal,
    handleToggleActiveKnowledgeSource,
    disactivatingKnowledgeSource,
    isOpenedActivateModal,
    onCloseActivateModal,
    onOpenActivateModal,
    handleUploadSourceFirecrawl,
    addingSourceFirecrawl,
    isOpenedSourceModal,
    onCloseSourceModal: handleCloseSourceModalWithCleanup,
    onOpenSourceModal,
    isOpenedEditChunkModal,
    onCloseEditChunkModal: handleCloseEditChunkModalWithCleanup,
    onOpenEditChunkModal,
    updatingChunk,
    editingChunk,
    handleEditChunk,
    handleSaveEditChunk,
    refreshTrigger,
    knowledgeBaseData,
    isLoadingBase,
    isSourceCreating:
      addingSourceFile || addingSourceText || addingSourceFirecrawl,
    handleDownloadKnowledgeSource,
    handleDownloadAllKnowledgeSources,
    downloadingKnowledgeSource,
    ...rest,
  };
};
